import { useCallback } from 'react'
import { useToast } from '@chakra-ui/react'
import apiService from '@/services/api'
import { Invitation, TOAST_DURATIONS } from '@/constants/invitations'
import { useApiResource } from './useApiResource'

interface UseInvitationsReturn {
  invitations: Invitation[]
  loading: boolean
  error: string | null
  loadInvitations: () => Promise<void>
  cancelInvitation: (invitationId: number) => Promise<void>
  refreshInvitations: () => Promise<void>
}

export function useInvitations(): UseInvitationsReturn {
  const { data: invitations, loading, error, refresh, setData: setInvitations } =
    useApiResource<Invitation[]>(
      async () => {
        const data = await apiService.getOrganizationInvitations()
        return data.map((invitation: any) => ({
          ...invitation,
          role: invitation.role as Invitation['role'],
          status: invitation.status as Invitation['status'],
        }))
      },
      [],
      { errorToastTitle: 'Error loading invitations', toastDuration: TOAST_DURATIONS.ERROR }
    )
  const toast = useToast()

  const cancelInvitation = useCallback(
    async (invitationId: number) => {
      const invitation = invitations.find((inv) => inv.id === invitationId)
      const invitationEmail = invitation?.email || 'invitation'

      try {
        setInvitations((current) => current.filter((inv) => inv.id !== invitationId))
        await apiService.cancelOrganizationInvitation(invitationId)
        toast({
          title: 'Invitation cancelled',
          description: `The invitation for ${invitationEmail} has been cancelled successfully`,
          status: 'success',
          duration: TOAST_DURATIONS.SUCCESS,
          isClosable: true,
        })
      } catch (err: any) {
        if (invitation) {
          setInvitations((current) => [...current, invitation].sort((a, b) => b.id - a.id))
        }
        const errorMessage = err.response?.data?.message || 'Failed to cancel invitation'
        toast({
          title: 'Error cancelling invitation',
          description: errorMessage,
          status: 'error',
          duration: TOAST_DURATIONS.ERROR,
          isClosable: true,
        })
        throw err
      }
    },
    [invitations, toast, setInvitations]
  )

  return {
    invitations,
    loading,
    error,
    loadInvitations: refresh,
    cancelInvitation,
    refreshInvitations: refresh,
  }
}

export default useInvitations
